import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';

/**
 * La etiqueta de estado.
 *
 * `13 §7` prohíbe comunicar un estado solo con color, así que la etiqueta
 * siempre lleva texto y admite un glifo. El tono es un refuerzo, nunca el
 * mensaje: eso importa en la matriz de permisos, que tiene tres desenlaces, y
 * en el tablero de tareas, donde el daltonismo más común confunde justo el par
 * verde/rojo que separa «hecho» de «bloqueado».
 */
const badge = cva(
  'inline-flex w-max items-center gap-1.5 rounded-full border font-bold leading-none whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'border-line bg-muted text-ink-soft',
        primary: 'border-primary/25 bg-primary-pale text-primary',
        success: 'border-success-line bg-success-pale text-success',
        warning: 'border-warning-line bg-warning-pale text-warning',
        danger: 'border-danger-line bg-danger-pale text-danger',
        info: 'border-info-line bg-info-pale text-info',
      },
      size: { sm: 'px-2 py-1 text-[0.62rem]', md: 'px-2.5 py-1.5 text-[0.68rem]' },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badge>['tone']>;

export function Badge({
  tone,
  size,
  icon,
  children,
  className,
  ...props
}: { icon?: ReactNode } & VariantProps<typeof badge> & HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={cn(badge({ tone, size }), className)} {...props}>
      {icon ? <span className="shrink-0 [&>svg]:size-3">{icon}</span> : null}
      {children}
    </span>
  );
}

/**
 * El punto de estado en vivo.
 *
 * El halo late solo cuando algo está de verdad ocurriendo. Un punto que late
 * siempre deja de significar nada en diez minutos de uso.
 */
export function LiveDot({ tone = 'success', live = true }: { tone?: BadgeTone; live?: boolean }) {
  const color = {
    neutral: 'bg-ink-faint',
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
    info: 'bg-info',
  }[tone];

  return (
    <span className="relative inline-flex size-2 shrink-0" aria-hidden="true">
      {live ? <span className={cn('animate-radar absolute inset-0 rounded-full', color)} /> : null}
      <span className={cn('relative size-2 rounded-full', color)} />
    </span>
  );
}
